// Off-main-thread camera → decode loop. Adapted from event-connect's scanner
// (use-scan-loop.ts): the rAF dispatch, ~15Hz rate limit + `pending` guard, viewfinder ROI crop,
// one getBoundingClientRect() per decode, and the one-way THOROUGH→LEAN decode-options downgrade
// all port over unchanged in spirit. What's adapted for this codebase:
//
// - No camera acquisition here. ProductScanStep.tsx already owns getUserMedia (requestCamera/
//   stopCamera, wired through useProductScan's four-screen contract) — this hook attaches the
//   already-granted `stream` to its own <video> element rather than acquiring a second one, so
//   there is exactly one getUserMedia call and exactly one place that stops its tracks.
// - camera-tuning.ts (resolution ladder, zoom/focus, lens picking) was NOT ported — see the
//   needs-decision note in this PR's description. That means `ScanTelemetryContext`'s
//   resRung/zoom*/focus/lens fields stay null; only decodeOptionsName is ever set here.
// - No corner/box overlay (event-connect's ScanOverlay) — out of scope for this port — so this
//   returns only what the step UI needs: the video ref to render, a hard error, and the
//   marginal-floor hint, rather than display-space Detection objects.

import { useEffect, useRef, useState } from 'react'
import type { Decoder, Rect } from './decoder/types'
import { THOROUGH, chooseDecodeOptions } from './decoder/decode-options'
import type { DecodeOptions } from './decoder/worker-protocol'
import { captureFrame, decideFrameTick } from './frame-tick'
import { evaluateFloor, expectedPxPerModule, measuredPxPerModule } from './scan-geometry'
import { ScanTelemetry, type ScanTelemetrySummary } from './telemetry'

const TARGET_INTERVAL_MS = 1000 / 15 // ~15 detect/s, not per animation frame

export interface UseScanLoopArgs {
  readonly decoder: Decoder | null
  readonly formats: readonly string[]
  /** The already-granted camera stream (see the file header) — null until permission lands. */
  readonly stream: MediaStream | null
  /** Gates the whole effect: no decode work while the step isn't showing a live camera. */
  readonly active: boolean
  readonly onDetected: (code: string) => void
}

export interface ScanLoopState {
  readonly videoRef: React.RefObject<HTMLVideoElement | null>
  readonly error: string | null
  /** Between the two decodable floors (scan-geometry.ts) — an actionable "move back a little"
   * signal, distinct from `error`'s give-up verdict. Not yet surfaced in the step's copy; kept
   * on the hook's return so a future UI pass can wire it without touching the loop again. */
  readonly marginal: boolean
  /** Read on demand rather than pushed into state, so recording a sample never triggers a render. */
  readonly telemetry: () => ScanTelemetrySummary
}

/** `setOptions` is on the implementation, not the frozen `Decoder` interface — only
 * WorkerDecoder has it; WasmDecoder always runs the conservative LEAN set (decode-options.ts). */
function decoderSetOptions(decoder: Decoder): ((options: DecodeOptions) => void) | null {
  const withOptions = decoder as Decoder & { setOptions?: (options: DecodeOptions) => void }
  return typeof withOptions.setOptions === 'function' ? withOptions.setOptions.bind(withOptions) : null
}

export function useScanLoop({ decoder, formats, stream, active, onDetected }: UseScanLoopArgs): ScanLoopState {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [marginal, setMarginal] = useState(false)
  // A ref, not state: written on every decode (~15/s) and must never cause a render, or the
  // measurement would perturb the thing it measures.
  const telemetryRef = useRef(new ScanTelemetry())
  const onDetectedRef = useRef(onDetected)
  onDetectedRef.current = onDetected

  useEffect(() => {
    if (!active || !decoder || !stream) return undefined
    const activeDecoder = decoder

    let rafId = 0
    let stopped = false
    let pending = false
    let lastDetectAt = 0
    let decodeStart = 0
    // The video's on-screen box only changes on resize/orientation, not per frame — read once
    // per accepted detect (~15/s) and cached here, rather than via a getBoundingClientRect()
    // forced-layout on every rAF tick (~60/s). Null until the first detect fills it; a resize is
    // reflected on the next detect — imperceptible for a viewfinder.
    let lastDisplay: { width: number; height: number } | null = null
    const canvas = document.createElement('canvas')
    const setDecodeOptions = decoderSetOptions(activeDecoder)
    let decodeOptions: DecodeOptions = THOROUGH

    const onError = (err: unknown) => {
      if (stopped) return
      telemetryRef.current.recordError()
      setError(err instanceof Error ? err.message : String(err))
    }

    function tick() {
      const video = videoRef.current
      const decision = decideFrameTick({
        now: performance.now(),
        lastDetectAt,
        intervalMs: TARGET_INTERVAL_MS,
        pending,
        video: video && { width: video.videoWidth, height: video.videoHeight, readyState: video.readyState },
        display: lastDisplay,
      })
      if (decision.kind !== 'detect') return
      lastDetectAt = decision.now
      captureAndDetect(video as HTMLVideoElement, decision.frame)
    }

    function captureAndDetect(video: HTMLVideoElement, frame: Rect) {
      const capture = captureFrame(video, canvas, frame)
      if (capture.resized) {
        telemetryRef.current.recordGeometry({ width: video.videoWidth, height: video.videoHeight }, frame)
      }
      if (!capture.ok) return

      pending = true
      decodeStart = performance.now()
      // The one getBoundingClientRect() per decode — cached for the next tick's ROI crop.
      const displayRect = video.getBoundingClientRect()
      lastDisplay = { width: displayRect.width, height: displayRect.height }
      activeDecoder
        .detect(canvas)
        .then(results => handleResults(results, frame))
        .catch(onError)
        .finally(() => {
          pending = false
        })
    }

    function handleResults(results: { rawValue: string; boundingBox: Rect }[], roi: Rect) {
      if (stopped) return
      // A decode round-trip completed, so the decoder and camera are working: drop any earlier
      // transient failure instead of leaving the alert up over a scanner that is scanning.
      setError(null)
      const durationMs = performance.now() - decodeStart
      const hasHit = results.length > 0
      telemetryRef.current.record(durationMs, hasHit, performance.now())
      if (hasHit) {
        telemetryRef.current.recordMeasuredPxPerModule(measuredPxPerModule(results[0].boundingBox.width))
        onDetectedRef.current(results[0].rawValue)
      }

      applyDecodeOptionsDowngrade()
      if (roi.width > 0) applyFloorVerdict(roi.width)
    }

    // One-way downgrade (never back within this session) so a slow device stops paying for
    // tryRotate/tryInvert once its own p95 says it can't afford them.
    function applyDecodeOptionsDowngrade() {
      if (!setDecodeOptions) return
      const next = chooseDecodeOptions(decodeOptions, telemetryRef.current.summary(), false)
      if (next === decodeOptions) return
      decodeOptions = next
      setDecodeOptions(next)
      telemetryRef.current.setContext({ decodeOptionsName: next === THOROUGH ? 'thorough' : 'lean' })
    }

    // Between the floors is "marginal": an actionable hint. Below the hard floor is only a
    // prediction (a fitted fill assumption, not a measurement) and must not raise `error`: a
    // 640x480 desktop webcam cropped into the 3:4 viewfinder predicts ~1.7 px/module yet decodes
    // fine. `error` is reserved for real failures (decoder init, play(), a rejected detect).
    function applyFloorVerdict(roiWidth: number) {
      const measured = telemetryRef.current.latestMeasuredPxPerModule()
      const pxPerModule = measured ?? expectedPxPerModule(roiWidth)
      setMarginal(evaluateFloor(pxPerModule) !== 'ok')
    }

    function loop() {
      if (stopped) return
      rafId = requestAnimationFrame(loop)
      tick()
    }

    async function start() {
      try {
        const el = videoRef.current
        if (!el || stopped) return
        el.srcObject = stream
        await el.play()
        if (stopped) return
        await activeDecoder.init(formats)
        if (stopped) return
        telemetryRef.current.setContext({ decodeOptionsName: setDecodeOptions ? 'thorough' : 'lean' })
        loop()
      } catch (err) {
        onError(err)
      }
    }

    start()

    return () => {
      stopped = true
      cancelAnimationFrame(rafId)
      activeDecoder.destroy()
    }
  }, [decoder, formats, stream, active])

  return {
    videoRef,
    error,
    marginal,
    telemetry: () => telemetryRef.current.summary(),
  }
}
